import {
  type IndustrialCaseDocument,
  hasIndustrialCaseBeforeAfter,
} from '@/domain/industrialCase/industrialCaseDocument';

import * as S from './IndustrialCaseDetailPage.styles';

/**
 * ### IndustrialCaseFactSectionLabels
 *
 * Headings for the factual sections, passed in so the live page can translate
 * them while the admin preview supplies the author's own language.
 */
export type IndustrialCaseFactSectionLabels = {
  readonly tasks: string;
  readonly siteConditions: string;
  readonly systemSetup: string;
  readonly beforeAfter: string;
  readonly before: string;
  readonly after: string;
  readonly measurement: string;
  readonly measurementConditions: string;
  readonly limitations: string;
};

/**
 * ### IndustrialCaseContextSections
 *
 * Renders what the customer set out to do and the conditions they had to do it
 * in: the tasks, the constraints of the site, and the shape of the deployment.
 *
 * Sits between the field challenges and the authored body, because it answers
 * "what was this plant like" before the article explains how it was solved.
 *
 * Every section is recommended rather than required, so each renders only when
 * it holds content and a case authored before these fields existed is unchanged.
 *
 * @param props The case being rendered and the section headings
 * @returns The context sections, in reading order
 */
export function IndustrialCaseContextSections({
  document,
  labels,
}: {
  document: IndustrialCaseDocument;
  labels: IndustrialCaseFactSectionLabels;
}) {
  return (
    <>
      <FactList heading={labels.tasks} items={document.tasks} />
      <FactList
        heading={labels.siteConditions}
        items={document.siteConditions}
      />
      <FactList heading={labels.systemSetup} items={document.systemSetup} />
    </>
  );
}

/**
 * ### IndustrialCaseResultSections
 *
 * Renders what changed and what the numbers do and do not claim: the
 * before-and-after comparison, then the measurement conditions and limitations.
 *
 * Sits directly after the outcome summary, so the qualifications are read with
 * the figures rather than after the reader has moved on.
 *
 * @param props The case being rendered and the section headings
 * @returns The result-qualifying sections, in reading order
 */
export function IndustrialCaseResultSections({
  document,
  labels,
}: {
  document: IndustrialCaseDocument;
  labels: IndustrialCaseFactSectionLabels;
}) {
  const hasMeasurementNotes =
    document.measurementConditions.trim() !== '' ||
    document.limitations.trim() !== '';

  return (
    <>
      {hasIndustrialCaseBeforeAfter(document.beforeAfter) && (
        <section className={S.proseSection}>
          <h2 className={S.sectionHeading}>{labels.beforeAfter}</h2>
          <div className={S.comparisonGrid}>
            <div className={S.comparisonCell}>
              <span className={S.comparisonLabel}>{labels.before}</span>
              <span className={S.comparisonValue}>
                {document.beforeAfter.before}
              </span>
            </div>
            <div className={S.comparisonCell}>
              <span className={S.comparisonLabel}>{labels.after}</span>
              <span className={S.comparisonValue}>
                {document.beforeAfter.after}
              </span>
            </div>
          </div>
        </section>
      )}

      {hasMeasurementNotes && (
        <section className={S.proseSection}>
          <h2 className={S.sectionHeading}>{labels.measurement}</h2>
          <dl className={S.noteList}>
            <Note
              term={labels.measurementConditions}
              description={document.measurementConditions}
            />
            <Note
              term={labels.limitations}
              description={document.limitations}
            />
          </dl>
        </section>
      )}
    </>
  );
}

/**
 * One bulleted section. Blank rows are dropped rather than rendered as empty
 * bullets, because a draft can hold a row the author never filled.
 */
function FactList({
  heading,
  items,
}: {
  heading: string;
  items: ReadonlyArray<string>;
}) {
  const filled = items.filter((item) => item.trim() !== '');
  if (filled.length === 0) return null;

  return (
    <section className={S.proseSection}>
      <h2 className={S.sectionHeading}>{heading}</h2>
      <ul className={S.factList}>
        {filled.map((item) => (
          <li className={S.factListItem} key={item}>
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Note({ term, description }: { term: string; description: string }) {
  if (description.trim() === '') return null;

  return (
    <div>
      <dt className={S.noteTerm}>{term}</dt>
      <dd className={S.noteDescription}>{description}</dd>
    </div>
  );
}
